import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useSlideIn } from '../hooks/useSlideIn';

const faqItems = [
  {
    question: 'Was kostet eine Elektroinstallation?',
    answer: 'Die Kosten einer Elektroinstallation hängen von verschiedenen Faktoren ab: Umfang der Arbeiten, Gebäudeart, verwendete Materialien und individuelle Anforderungen. Wir erstellen Ihnen gerne ein transparentes und unverbindliches Angebot nach einer Vor-Ort-Besichtigung. Kontaktieren Sie uns für eine kostenlose Beratung.',
  },
  {
    question: 'Arbeiten Sie nur in Wuppertal?',
    answer: 'Unser Hauptstandort ist Wuppertal, aber wir sind deutschlandweit tätig. Zu unseren Projektstandorten gehören unter anderem Oberhausen, Köln, Kassel, Bremen, Bergisch Gladbach und Regensburg. Sprechen Sie uns an – wir prüfen gerne, ob wir Ihr Projekt betreuen können.',
  },
  {
    question: 'Bieten Sie Vor-Ort-Beratung an?',
    answer: 'Ja, wir bieten kostenlose Vor-Ort-Beratungen an. Ein Fachmann aus unserem Team kommt zu Ihnen, analysiert die Situation und bespricht mit Ihnen die besten Lösungen für Ihr Projekt. Vereinbaren Sie einfach einen Termin über unser Kontaktformular oder telefonisch.',
  },
  {
    question: 'Wie schnell sind Termine möglich?',
    answer: 'Wir bemühen uns, zeitnah für Sie da zu sein. Bei dringenden Fällen versuchen wir, innerhalb von 24-48 Stunden einen Termin zu finden. Für Planungsprojekte empfehlen wir eine Vorlaufzeit von 1-2 Wochen. Kontaktieren Sie uns, und wir finden gemeinsam eine Lösung.',
  },
  {
    question: 'Welche Zertifizierungen hat Ihr Betrieb?',
    answer: 'Wir sind ein zertifizierter Fachbetrieb nach DIN ISO 9001 (Qualitätsmanagement) und DIN 14675 (Brandmeldeanlagen und Sprachalarmierungsanlagen). Diese Zertifizierungen garantieren Ihnen höchste Qualitätsstandards und normgerechte Ausführung aller Arbeiten.',
  },
  {
    question: 'Führen Sie auch DGUV V3 Prüfungen durch?',
    answer: 'Ja, wir führen professionelle Prüfungen elektrischer Anlagen und Betriebsmittel nach DGUV Vorschrift 3 durch. Dies umfasst ortsveränderliche Geräte, ortsfeste Anlagen und Maschinen. Sie erhalten eine rechtssichere Dokumentation und Prüfprotokolle.',
  },
  {
    question: 'Bieten Sie Photovoltaik-Anlagen an?',
    answer: 'Ja, wir planen und installieren maßgeschneiderte Photovoltaikanlagen für private, gewerbliche und industrielle Anwendungen. Von der Erstberatung über die Planung bis zur Installation und Inbetriebnahme begleiten wir Sie durch den gesamten Prozess.',
  },
  {
    question: 'Wie läuft eine Zusammenarbeit ab?',
    answer: 'Unser Prozess besteht aus fünf Schritten: 1. Kostenlose Erstberatung, 2. Transparentes Angebot, 3. Professionelle Planung, 4. Fachgerechte Ausführung, 5. Gemeinsame Abnahme. So garantieren wir Ihnen einen reibungslosen Projektverlauf mit höchster Qualität.',
  },
];

export const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const anim = useSlideIn();

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" data-testid="faq-section" className="py-20 md:py-32 bg-zinc-50">
      <div ref={anim.ref} className={"max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 slide-in-right" + (anim.isVisible ? " visible" : "")}>
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-[#E31E24] font-semibold text-sm uppercase tracking-widest mb-4">
            FAQ
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-neutral-900 mb-6">
            Häufig gestellte Fragen
          </h2>
          <p className="text-neutral-600 text-base lg:text-lg max-w-2xl mx-auto">
            Hier finden Sie Antworten auf die am häufigsten gestellten Fragen rund um unsere Dienstleistungen.
          </p>
        </div>

        {/* FAQ Items */}
        <div className="bg-white border border-zinc-200">
          {faqItems.map((item, index) => (
            <div
              key={index}
              data-testid={`faq-item-${index}`}
              className={`faq-item border-b border-zinc-200 last:border-0 ${openIndex === index ? 'open' : ''}`}
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-zinc-50 transition-colors"
                aria-expanded={openIndex === index}
              >
                <span className="font-bold text-neutral-900 pr-4">
                  {item.question}
                </span>
                <ChevronDown 
                  className={`faq-arrow w-5 h-5 text-[#E31E24] flex-shrink-0 transition-transform duration-300 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                />
              </button>
              
              <div 
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96' : 'max-h-0'
                }`}
              >
                <div className="px-6 pb-6 text-neutral-600 leading-relaxed">
                  {item.answer}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <p className="text-neutral-600 mb-4">
            Haben Sie weitere Fragen?
          </p>
          <a
            href="#kontakt"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector('#kontakt')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
            data-testid="faq-cta"
            className="btn-primary inline-block"
          >
            Kontaktieren Sie uns
          </a>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
